import { on } from '@ngrx/store';

import { BlueSageTerminalPushActions } from './bluesage-terminal-push.actions';
import {
    BlueSagePushPhase,
    BlueSageTerminalPushFeatureState,
    blueSageTerminalPushInitialState,
} from './bluesage-terminal-push.state';

/**
 * Wire status → what the operator is actually waiting on.
 *
 * `completed` is deliberately NOT always "paid": an `auth` that completes is
 * money HELD, not taken, and a panel that said "Paid" would put money on the
 * job the merchant has not received and cannot spend. That distinction is the
 * whole reason `kind` is carried through this feature.
 */
function phaseFor(
    status: string | null | undefined,
    settled: boolean,
    kind: BlueSageTerminalPushFeatureState['kind'],
): BlueSagePushPhase {
    if (!settled) { return 'waiting'; }

    switch (status) {
        case 'completed': return kind === 'auth' ? 'authorized' : 'settled';
        case 'declined': return 'declined';
        case 'cancelled': return 'cancelled';
        case 'timeout': return 'timedOut';
        default:
            // Settled with a status we do not recognise. The code list is open, so
            // this must degrade to something honest rather than being read as
            // success — claiming a payment that may not have happened is the one
            // outcome worth being paranoid about.
            return 'failed';
    }
}

const startPush = (
    state: BlueSageTerminalPushFeatureState,
    { transactionId, epi, amountMinor }: { transactionId: string; epi: string; amountMinor: number },
    kind: BlueSageTerminalPushFeatureState['kind'],
): BlueSageTerminalPushFeatureState => ({
    ...state,
    phase: 'sending',
    transactionId,
    epi,
    kind,
    amountMinor,
    pushId: undefined,
    // The clock starts HERE, not when a push id arrives. The publish long-polls
    // for the whole customer interaction, so a clock that waited for the id
    // would sit at zero for exactly the minutes an operator needs to see.
    //
    // It is NOT restarted when the panel was already counting through
    // `preparing`: creating the transaction is part of the wait the operator is
    // sitting through, and a counter that jumped back to 0s when the record
    // finally saved would understate it by however long that took.
    startedAt: state.phase === 'preparing' ? (state.startedAt ?? Date.now()) : Date.now(),
    elapsedSeconds: state.phase === 'preparing' ? state.elapsedSeconds : 0,
    transactionDiscarded: false,
    // The flag describes the CURRENT attempt. Leaving a previous failure on
    // screen through a request that is working is the same defect the review
    // queue shipped with.
    message: undefined,
    cancelling: false,
});

export const blueSageTerminalPushReducers = [

    // Pressed Pay or Authorize. The transaction is being written; nothing has
    // reached BlueSage, let alone a device. The clock starts here because this
    // is where the operator's wait starts.
    on(
        BlueSageTerminalPushActions.pushPreparing,
        (state: BlueSageTerminalPushFeatureState, { epi, amountMinor, kind }): BlueSageTerminalPushFeatureState => ({
            ...blueSageTerminalPushInitialState,
            phase: 'preparing',
            epi,
            amountMinor,
            kind,
            startedAt: Date.now(),
        }),
    ),

    on(
        BlueSageTerminalPushActions.sendToTerminalButtonClicked,
        (state: BlueSageTerminalPushFeatureState, action): BlueSageTerminalPushFeatureState =>
            startPush(state, action, 'sale'),
    ),

    on(
        BlueSageTerminalPushActions.authorizeButtonClicked,
        (state: BlueSageTerminalPushFeatureState, action): BlueSageTerminalPushFeatureState =>
            startPush(state, action, 'auth'),
    ),

    on(
        BlueSageTerminalPushActions.completePreauthConfirmed,
        (state: BlueSageTerminalPushFeatureState, action): BlueSageTerminalPushFeatureState =>
            startPush(state, action, 'ticket'),
    ),

    // A retry, or the payment dialog's create-then-push. Carries its own kind
    // rather than being inferred from which button was pressed.
    on(
        BlueSageTerminalPushActions.pushRequested,
        (state: BlueSageTerminalPushFeatureState, action): BlueSageTerminalPushFeatureState =>
            startPush(state, action, action.kind),
    ),

    on(
        BlueSageTerminalPushActions.pushSuccess,
        (state: BlueSageTerminalPushFeatureState, { result, kind }): BlueSageTerminalPushFeatureState => ({
            ...state,
            kind,
            pushId: result.push.pushId ?? undefined,
            phase: phaseFor(result.push.status, result.push.settled, kind),
            // Only once it is genuinely on the device does the clock mean anything.
            startedAt: result.push.settled ? undefined : Date.now(),
            elapsedSeconds: 0,
        }),
    ),

    on(
        BlueSageTerminalPushActions.completeSuccess,
        (state: BlueSageTerminalPushFeatureState, { result }): BlueSageTerminalPushFeatureState => ({
            ...state,
            kind: 'ticket',
            pushId: result.push.pushId ?? undefined,
            phase: phaseFor(result.push.status, result.push.settled, 'ticket'),
            startedAt: result.push.settled ? undefined : Date.now(),
            elapsedSeconds: 0,
        }),
    ),

    on(
        BlueSageTerminalPushActions.completeError,
        BlueSageTerminalPushActions.pushError,
        (state: BlueSageTerminalPushFeatureState, { error }): BlueSageTerminalPushFeatureState => ({
            ...state,
            phase: 'failed',
            startedAt: undefined,
            // The server already mapped `body.code` to something an operator can
            // act on. Never a raw code on screen.
            message: error?.message,
        }),
    ),

    // The same refusal, arriving through the status read instead of the
    // mutation. Same screen, same message.
    on(
        BlueSageTerminalPushActions.pushRefusedAfterStart,
        (state: BlueSageTerminalPushFeatureState, { message }): BlueSageTerminalPushFeatureState => ({
            ...state,
            phase: 'failed',
            startedAt: undefined,
            message,
        }),
    ),

    // A push followed through its transaction. `in_flight` keeps the clock
    // running; everything else settles the phase. `failed` is handled by
    // `pushRefusedAfterStart`, which the effect dispatches instead, so the
    // cancel logic has one action to key on.
    on(
        BlueSageTerminalPushActions.pushStatusChecked,
        (state: BlueSageTerminalPushFeatureState, { status }): BlueSageTerminalPushFeatureState => {
            if (status.outcome === 'in_flight' || status.outcome === 'none' || status.outcome === 'failed') {
                return {
                    ...state,
                    pushId: status.push.pushId ?? state.pushId,
                };
            }

            const phase: BlueSagePushPhase = status.outcome === 'lost'
                ? 'lost'
                : phaseFor(status.push.status, true, state.kind);

            return {
                ...state,
                pushId: status.push.pushId ?? state.pushId,
                phase,
                startedAt: undefined,
                cancelling: false,
            };
        },
    ),

    on(
        BlueSageTerminalPushActions.pushStatusPolled,
        (state: BlueSageTerminalPushFeatureState, { push }): BlueSageTerminalPushFeatureState => ({
            ...state,
            pushId: push.pushId ?? state.pushId,
            phase: phaseFor(push.status, push.settled, state.kind),
            // Settling stops the clock; the elapsed figure stays on screen so the
            // operator can see how long the customer took.
            startedAt: push.settled ? undefined : (state.startedAt ?? Date.now()),
            cancelling: push.settled ? false : state.cancelling,
        }),
    ),

    on(
        BlueSageTerminalPushActions.elapsedTicked,
        (state: BlueSageTerminalPushFeatureState): BlueSageTerminalPushFeatureState => ({
            ...state,
            elapsedSeconds: state.startedAt
                ? Math.floor((Date.now() - state.startedAt) / 1000)
                : state.elapsedSeconds,
        }),
    ),

    on(
        BlueSageTerminalPushActions.cancelButtonClicked,
        (state: BlueSageTerminalPushFeatureState): BlueSageTerminalPushFeatureState => ({
            ...state,
            cancelling: true,
        }),
    ),

    on(
        BlueSageTerminalPushActions.cancelSuccess,
        (state: BlueSageTerminalPushFeatureState): BlueSageTerminalPushFeatureState => ({
            ...state,
            cancelling: false,
            phase: 'cancelled',
            startedAt: undefined,
        }),
    ),

    on(
        BlueSageTerminalPushActions.cancelError,
        (state: BlueSageTerminalPushFeatureState, { error }): BlueSageTerminalPushFeatureState => ({
            ...state,
            cancelling: false,
            // `push_not_cancellable` is NOT a failure — it means the device already
            // finished. The phase deliberately stays where it was so the poller can
            // report the real outcome, and the message says so.
            message: error?.message,
        }),
    ),

    // Nothing was sent and the draft transaction has been cancelled, so there is
    // nothing left for Try again to replay.
    on(
        BlueSageTerminalPushActions.failedTransactionDiscarded,
        (state: BlueSageTerminalPushFeatureState): BlueSageTerminalPushFeatureState => ({
            ...state,
            transactionId: undefined,
            transactionDiscarded: true,
        }),
    ),

    on(
        BlueSageTerminalPushActions.panelDismissed,
        BlueSageTerminalPushActions.doneButtonClicked,
        (): BlueSageTerminalPushFeatureState => blueSageTerminalPushInitialState,
    ),
];
